import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { AcceptTermsPage } from './pages/AcceptTermsPage';
import { SSOCallbackPage } from './pages/SSOCallbackPage';
import { DashboardPage } from './pages/DashboardPage';
import { QuestionnairePage } from './pages/QuestionnairePage';
import { PracticeListPage } from './pages/PracticeListPage';
import { PracticeSessionPage } from './pages/PracticeSessionPage';
import { NewPracticePage } from './pages/NewPracticePage';
import { PracticeScheduleLobbyPage } from './pages/PracticeScheduleLobbyPage';
import { PersonasPage } from './pages/PersonasPage';
import { GoalsPage } from './pages/GoalsPage';
import { SettingsPage } from './pages/SettingsPage';
import { BillingPage } from './pages/BillingPage';
import { BillingLockedPage } from './pages/BillingLockedPage';
import { BillingSuccessPage } from './pages/BillingSuccessPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminPersonasPage } from './pages/admin/AdminPersonasPage';
import { AdminGoalsPage } from './pages/admin/AdminGoalsPage';
import { AdminPromoCodesPage } from './pages/admin/AdminPromoCodesPage';
import { AdminTenantsPage } from './pages/admin/AdminTenantsPage';
import { AdminTenantDetailPage } from './pages/admin/AdminTenantDetailPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';
import { AdminVideoAvatarsPage } from './pages/admin/AdminVideoAvatarsPage';
import { ProfilePage } from './pages/ProfilePage';
import { InviteAcceptPage } from './pages/InviteAcceptPage';
import { TeamDashboardPage } from './pages/TeamDashboardPage';
import { TeamMemberPage } from './pages/TeamMemberPage';

const queryClient = new QueryClient();

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/auth/accept-terms" element={<AcceptTermsPage />} />
              <Route path="/auth/sso/callback" element={<SSOCallbackPage />} />
              <Route path="/invite/:token" element={<InviteAcceptPage />} />

              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<DashboardPage />} />
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute requireOrgRole={['owner', 'admin']}>
                      <QuestionnairePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/practice" element={<PracticeListPage />} />
                <Route path="/practice/new" element={<NewPracticePage />} />
                <Route path="/practice/schedule/:id" element={<PracticeScheduleLobbyPage />} />
                <Route path="/practice/:id" element={<PracticeSessionPage />} />
                <Route path="/personas" element={<PersonasPage />} />
                <Route
                  path="/team"
                  element={
                    <ProtectedRoute requireOrgRole={['owner', 'admin', 'manager']}>
                      <TeamDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/team/:userId"
                  element={
                    <ProtectedRoute requireOrgRole={['owner', 'admin', 'manager']}>
                      <TeamMemberPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/goals" element={<GoalsPage />} />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute requireOrgRole={['owner', 'admin']}>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing"
                  element={
                    <ProtectedRoute requireOrgRole={['owner', 'admin']}>
                      <BillingPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/billing/locked" element={<BillingLockedPage />} />
                <Route path="/billing/success" element={<BillingSuccessPage />} />
              </Route>

              <Route
                element={
                  <ProtectedRoute requirePlatformAdmin>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/personas" element={<AdminPersonasPage />} />
                <Route path="/admin/video-avatars" element={<AdminVideoAvatarsPage />} />
                <Route path="/admin/goals" element={<AdminGoalsPage />} />
                <Route path="/admin/promo-codes" element={<AdminPromoCodesPage />} />
                <Route path="/admin/tenants" element={<AdminTenantsPage />} />
                <Route path="/admin/tenants/:id" element={<AdminTenantDetailPage />} />
                <Route path="/admin/settings" element={<AdminSettingsPage />} />
                <Route path="/profile" element={<ProfilePage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
